import type { UIMessage } from '@ai-sdk/react';
import {
  settledToolPartFields,
  type ChatMessageEnvelope,
  type ChatMessagePart
} from '@bliks-io/contracts';

/**
 * A chat message as the history API returns it — the app-owned envelope from
 * `@bliks-io/contracts`, which a chat surface extends with its own role enum
 * and the `content` text projection.
 *
 * Every envelope field is OPTIONAL here and nowhere else. A bundle newer than
 * the backend it talks to — a rolling deploy, a rollback — still receives
 * content-only rows that carry no `parts`, no `id` and no `schemaVersion`
 * (plan 009 §6.3), and a reader that assumed them would render an empty
 * transcript.
 */
export type ChatHistoryMessage = Partial<ChatMessageEnvelope> &
  Pick<ChatMessageEnvelope, 'role'> & { content?: string };

type UIMessagePart = UIMessage['parts'][number];

/**
 * Widens a persisted part into the AI SDK's part shape. `input`/`output` are
 * optional on the envelope but required on a tool part, so they are named
 * explicitly rather than spread — and nothing else from the persisted part is
 * copied, keeping the allowlist the envelope defines intact.
 *
 * The final branch is unreachable for the types the contract knows today, but
 * not at runtime: a newer backend may persist a part type this bundle has never
 * heard of. It rides along untouched — `isRenderablePart` decides what actually
 * reaches the screen, so an unrecognized part is inert rather than fatal.
 */
function toUIMessagePart(part: ChatMessagePart): UIMessagePart {
  if (part.type === 'text') {
    return { type: 'text', text: part.text };
  }
  if (part.type === 'file') {
    // A reloaded attachment renders from its NAME, not its bytes: the object is
    // private, ephemeral and only ever read server-side, so there is no URL the
    // browser could hold. The SDK's part requires one, so it stays empty rather
    // than pointing at something that would 403 — or, worse, at a URL that
    // handed an untrusted document to the browser.
    return {
      type: 'file',
      mediaType: part.mediaType,
      filename: part.filename,
      url: ''
    };
  }
  if (
    part.type === 'tool-renderChart' ||
    part.type === 'tool-generateDocument' ||
    part.type === 'tool-renderEntityView' ||
    part.type === 'tool-app'
  ) {
    // `tool-app` (an installed app's chat tool) keeps only the frame: the SDK's
    // tool part has no home for `toolName`, and this bundle knows nothing about
    // what an app's tool does. It converts so a reloaded app-chat turn is a
    // well-formed SDK message; whether it RENDERS is `isRenderablePart`'s call,
    // and today it does not.
    return {
      type: part.type,
      toolCallId: part.toolCallId,
      input: part.input,
      // The SDK's error part is the shape `ToolPartBlock` draws its error card
      // from — see `settledToolPartFields`.
      ...settledToolPartFields(part)
    };
  }
  return part;
}

/**
 * The parts a reloaded turn renders from. Typed parts win; a legacy row that
 * only carries `content` is widened to a single text part, which is exactly
 * what the pre-parts frontend always did.
 */
function partsFromHistory(message: ChatHistoryMessage): UIMessagePart[] {
  if (message.parts && message.parts.length > 0) {
    return message.parts.map(toUIMessagePart);
  }
  return [{ type: 'text', text: message.content ?? '' }];
}

/**
 * Convert backend chat history messages to AI SDK's UIMessage[] format.
 * This allows seamless continuation of existing chat history with the useChat hook.
 *
 * The whole message-level metadata block rides along on `metadata` — structured
 * `entityRefs` so the answer renders its source chips without re-parsing
 * markdown, `langfuseTraceId` so feedback on a reloaded answer still scores the
 * trace that produced it, `aiProvenance` so a reloaded answer keeps its EU
 * AI-Act mark, and `userFeedback` so a reloaded answer shows the vote already
 * cast on it. Dropping any of them here would strand it: the history API is
 * the only place they ever arrive from.
 *
 * The persisted id is KEPT, never re-derived: the transport sends only the
 * trailing message, so its id is the turn key the backend writes under. Minting
 * a positional id here would make every reloaded message unrecognizable to the
 * server, and `regenerate` would append a duplicate turn instead of replacing
 * the answer it is redoing. Positional ids remain only for rows that have none.
 */
export function convertHistoryToUIMessages(messages: ChatHistoryMessage[]): UIMessage[] {
  return messages.map((msg, index) => ({
    id: msg.id ?? `history-${index}`,
    role: msg.role,
    parts: partsFromHistory(msg),
    metadata: {
      entityRefs: msg.entityRefs,
      langfuseTraceId: msg.langfuseTraceId,
      aiProvenance: msg.aiProvenance,
      userFeedback: msg.userFeedback
    }
  }));
}
